import { useMemo, useState } from "react";
import { Card } from "@tremor/react";
import { Filter, Globe } from "lucide-react";
import {
  useFindings, useProperties, useUpdateFindingStatus,
  type Finding, type Severity, type FindingStatus,
} from "@/lib/queries";
import { FindingMini } from "./Audits";
import { cn } from "@/lib/utils";

const SEVERITIES: Severity[] = ["critical", "high", "medium", "low", "info"];
const STATUSES: FindingStatus[] = ["open", "acknowledged", "resolved", "snoozed"];

export default function FindingsPage() {
  const [status, setStatus] = useState<FindingStatus | "">("open");
  const [severity, setSeverity] = useState<Severity | "">("");
  const [propertyId, setPropertyId] = useState<string>("");
  const properties = useProperties({ include_inactive: true });
  const list = useFindings({
    status: status || undefined,
    severity: severity || undefined,
    property_id: propertyId ? Number(propertyId) : undefined,
    limit: 100,
  });
  const update = useUpdateFindingStatus();

  const propertyOptions = useMemo(() => {
    return (properties.data?.items ?? []).map((p) => ({
      value: String(p.id),
      label: p.is_active ? p.domain : `${p.domain} (inactive)`,
    }));
  }, [properties.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Findings</h1>
        <p className="text-sm text-muted-foreground">
          Actionable signals extracted from audits. Severity-sorted; click to triage.
        </p>
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <FilterPicker label="Status" options={STATUSES} value={status} onChange={(v) => setStatus(v as FindingStatus | "")} />
          <FilterPicker label="Severity" options={SEVERITIES} value={severity} onChange={(v) => setSeverity(v as Severity | "")} />
          <FilterPicker
            label="Property"
            options={propertyOptions}
            value={propertyId}
            onChange={setPropertyId}
          />
          <span className="ml-auto text-xs text-muted-foreground">
            {list.data?.total ?? 0} finding{list.data?.total === 1 ? "" : "s"}
          </span>
        </div>
      </Card>

      <div className="space-y-2">
        {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!list.isLoading && (list.data?.items.length ?? 0) === 0 && (
          <Card>
            <p className="text-sm text-muted-foreground">
              No findings match these filters. {status === "open" ? "Looks clean." : ""}
            </p>
          </Card>
        )}
        {list.data?.items.map((f) => (
          <FindingTriageRow
            key={f.id}
            f={f}
            onUpdate={(s) => update.mutate({ id: f.id, status: s })}
          />
        ))}
      </div>
    </div>
  );
}


type FilterOption = string | { value: string; label: string };

function FilterPicker({ label, options, value, onChange }: {
  label: string; options: FilterOption[]; value: string; onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <span className="text-muted-foreground">{label}:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-input bg-background px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <option value="">all</option>
        {options.map((opt) => {
          const v = typeof opt === "string" ? opt : opt.value;
          const l = typeof opt === "string" ? opt : opt.label;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </label>
  );
}


function FindingTriageRow({ f, onUpdate }: { f: Finding; onUpdate: (s: FindingStatus) => void }) {
  const [showActions, setShowActions] = useState(false);
  return (
    <div className="space-y-2">
      <FindingMini f={f} />
      <div className="ml-3 flex items-center gap-2 text-[11px] text-muted-foreground">
        {f.property_domain && (
          <span className="inline-flex items-center gap-1">
            <Globe className="h-2.5 w-2.5" />
            <span className={cn(!f.property_active && "text-muted-foreground/60 line-through")}>
              {f.property_domain}
            </span>
            {f.property_active === false && (
              <span className="text-muted-foreground/60">(inactive)</span>
            )}
          </span>
        )}
        <span>status: <span className={cn(
          "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
          f.status === "open" && "bg-amber-500/20 text-amber-700 dark:text-amber-400",
          f.status === "resolved" && "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400",
          f.status === "acknowledged" && "bg-blue-500/20 text-blue-700 dark:text-blue-400",
          f.status === "snoozed" && "bg-muted text-muted-foreground",
        )}>{f.status}</span></span>
        <button
          type="button"
          onClick={() => setShowActions((v) => !v)}
          className="ml-auto rounded-md border border-border bg-background px-2 py-0.5 text-[10px] hover:bg-accent"
        >
          {showActions ? "hide" : "triage"}
        </button>
      </div>
      {showActions && (
        <div className="ml-3 flex flex-wrap gap-1.5">
          {STATUSES.filter((s) => s !== f.status).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { onUpdate(s); setShowActions(false); }}
              className="rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-accent"
            >
              mark {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
